import React from 'react';
import classnames from 'classnames';
import Draggable from 'react-draggable';
import {
  withMobileContext,
  WithMobileContextProps,
  withPerspectiveContext,
  PerspectiveContextProps,
} from '@wix/da-shared-react/pkg/Context';
import { PapiDeviation } from '@wix/da-types-papi';
import { isSourceImageVertical } from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/Image/_helpers';
import MediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';

import {
  withFeatureFlags,
  WithFeatureFlagsProps,
} from '@wix/da-shared-react/pkg/redux/flags';
import s from './ProfileHero.scss';

export interface Props {
  coverDeviation?: PapiDeviation;
  coverDeviationidOffsetY: number;
  className?: string;
  showProfileHeroAid?: boolean;
  isReducedHero?: boolean;
}
export interface State {
  bestFitImageLoaded: boolean;
}

export const INITIAL_SCROLL_TOP =
  parseInt(s['full-height']) - parseInt(s['initial-height']);
export const INITIAL_HEIGHT = s['initial-height'];
export const FULL_HEIGHT = s['full-height'];

export enum ProfileHeroHeightNames {
  Initial,
  Full,
}

// these heights are not easy-derivable from CSS/layout
export function getHeightTillSubnavbar(
  height: ProfileHeroHeightNames,
  reducedHero = false
) {
  switch (height) {
    case ProfileHeroHeightNames.Full:
      return reducedHero ? 692 : 800;

    case ProfileHeroHeightNames.Initial:
    default:
      return reducedHero ? 292 : 400;
  }
}

class ProfileHero extends React.Component<
  Props &
    WithFeatureFlagsProps &
    WithMobileContextProps &
    PerspectiveContextProps,
  State
> {
  heroWrapDiv: HTMLDivElement | null;

  readonly state: State = {
    bestFitImageLoaded: false,
  };

  componentDidMount() {
    const { isMobile, coverDeviation, flags } = this.props;

    this.loadBestFitImage();

    const doNotAddSpaceToScrollUp =
      flags.includes('no_missing_hero_scroll') && !coverDeviation;

    if (doNotAddSpaceToScrollUp === false) {
      this.addSpaceToScrollUp();
    }

    if (!isMobile) {
      window.onbeforeunload = this.restoreToInitScrollTopIfScrolledUp;
    }
  }

  componentDidUpdate(prevProps) {
    const { coverDeviation } = this.props;
    if (coverDeviation && coverDeviation !== prevProps.coverDeviation) {
      this.loadBestFitImage();
    }
    this.addSpaceToScrollUp();
  }

  /*
  scrollUp feature works like this:

  Hero Image and Content reside on different layers (scroll together but at different speed based on z coordinate)
  and are positioned independetly

  SSR:
  scrollTop: 0 (page is not scrollable up)
  contentPaddingTop: 0
  marginTop: -500 on 1000 height Hero (Hero is visible from the middle)

  when mounts, instantly change to:

  scrollTop: 500
  contentPaddingTop: 500
  marginTop: 0 on Hero (Hero is visible from the middle)

  so we keep the same position on Content and Hero relative to Window but now with some space above

  otherwise the extra space will be visible on SSR
  and when JS kicks in it will scroll down to where it should be to allow scrolling up.
  and we don't want such jumps on page load.
  */
  addSpaceToScrollUp = () => {
    const {
      isParallaxEnabled,
      getScrollableContainer,
      getContentContainer,
    } = this.props;

    const scrollableContainer = getScrollableContainer();
    const contentContainer = getContentContainer();

    if (!isParallaxEnabled) {
      return;
    }

    const heroWrapDivMarginTop = this.heroWrapDiv!.style.marginTop || '';
    // already done
    if (heroWrapDivMarginTop === '0px') {
      return;
    }

    // remove negative margin to allow scrolling up (hero layer)
    this.heroWrapDiv!.style.marginTop = '0px';

    // add space above content to allow scrolling up (content layer)
    contentContainer!.style.marginTop = `${INITIAL_SCROLL_TOP}px`;

    if (!window.location.hash) {
      scrollableContainer &&
        scrollableContainer.scrollTo &&
        scrollableContainer.scrollTo(0, INITIAL_SCROLL_TOP);
    }
  };

  restoreToInitScrollTopIfScrolledUp = () => {
    const { getScrollableContainer } = this.props;
    const scrollableContainer = getScrollableContainer();
    if (!scrollableContainer) {
      return;
    }
    // after you scrolled the page, the browser retains the scroll position on refresh
    // and that screws up the "scroll up to expand hero" feature
    // making it ridiculously big on initial load
    if (scrollableContainer.scrollTop < INITIAL_SCROLL_TOP) {
      scrollableContainer.scrollTo &&
        scrollableContainer.scrollTo(0, INITIAL_SCROLL_TOP);
    }
  };

  loadBestFitImage = () => {
    const bestFitImage = this.getBestFitImage();
    if (bestFitImage) {
      const img = new Image();
      img.onload = () => this.setState({ bestFitImageLoaded: true });
      img.src = bestFitImage.src;
    }
  };

  getBestFitImage = () => {
    const { coverDeviation } = this.props;
    const heroWrapDiv = this.heroWrapDiv!;

    if (!coverDeviation) {
      return;
    }

    const bestFitImage = MediaService.getSmallestThumbThatFullyCovers(
      coverDeviation,
      heroWrapDiv.clientWidth,
      heroWrapDiv.clientHeight,
      t => t[t.length - 1], // Take largest if nothing covers,
      { allowFullview: true, allowPreview: true }
    );

    return bestFitImage;
  };

  getSmallestImage = () => {
    const { coverDeviation } = this.props;
    return (
      coverDeviation &&
      coverDeviation.files &&
      MediaService.getSmallestThumb(coverDeviation)
    );
  };

  render = () => (
    <div
      ref={el => (this.heroWrapDiv = el)}
      className={classnames(this.props.className, s['hero-wrap'])}
    >
      {this.props.coverDeviation
        ? this.renderCoverImageWithWrapper()
        : this.renderCoverImagePlaceholder()}
    </div>
  );

  renderCoverImageWithWrapper = () => {
    const { isReducedHero } = this.props;
    const coverImage = this.renderCoverImage();
    if (isReducedHero) {
      if (!coverImage) {
        return null;
      }
      return (
        <Draggable defaultPosition={{ x: 0, y: 0 }}>{coverImage}</Draggable>
      );
    }
    return coverImage;
  };

  renderCoverImage = () => {
    const { coverDeviation, coverDeviationidOffsetY } = this.props;
    const { bestFitImageLoaded } = this.state;

    if (!coverDeviation) {
      return null;
    }

    const backgroundImage =
      (bestFitImageLoaded && this.getBestFitImage()) || this.getSmallestImage();

    if (!backgroundImage) {
      return null;
    }

    return isSourceImageVertical(coverDeviation) ? (
      <img
        src={backgroundImage.src}
        style={{ transform: `translateY(-${coverDeviationidOffsetY / 10}%)` }}
        className={classnames(
          s['hero'],
          s['is-vertical'],
          bestFitImageLoaded && s['no-blur']
        )}
        alt=""
      />
    ) : (
      <div
        style={{ backgroundImage: `url("${backgroundImage.src}")` }}
        className={classnames(
          s['hero'],
          s['is-horizontal'],
          bestFitImageLoaded && s['no-blur']
        )}
      />
    );
  };

  renderCoverImagePlaceholder = () => (
    <div
      className={classnames(
        s['hero'],
        !this.props.showProfileHeroAid && s['no-image'],
        this.props.showProfileHeroAid && s['is-ftue'],
        s['no-blur']
      )}
    >
      {this.props.showProfileHeroAid && <div className={s['meteors']} />}
    </div>
  );
}

export default withPerspectiveContext(
  withMobileContext(withFeatureFlags(ProfileHero))
);
